// Guidance for the team (docs/GUIDANCE.md): the Start here task cards, the Help panel's steps for each screen, and the
// "Show me" walkthroughs that point at the real buttons. Plain, short English; one action per step.
//
// TASKS     what someone wants to get done, in the order a sales day runs. `perm` is the page grant it needs
//           (canAccess); `tour` names a walkthrough in TOURS.
// SCREENS   matched to the current address (first match wins; `match` is a path prefix, `exact` for "/").
// TOURS     steps: `to` (open this screen first), `target` (a CSS selector on the real screen), `text`, and
//           `waitClick` (the person taps the highlighted button to go on, e.g. one that opens a form).

const ADD = '[data-testid="topbar-add-btn"]';

export const TASKS = [
  {
    id: "walkin", title: "A customer walked in", blurb: "Write down every visitor, even if they only look around.",
    perm: "visitors", to: "/visitors", tour: "walkin",
    steps: [
      "Open Visitors.",
      "Tap “Log Visitor”.",
      "Type their mobile number first. If they came before, the CRM shows them: use that.",
      "Fill in name, area, what they are looking for and who referred them (architect).",
      "Under “Interested in”, tap every division they want: MAP, Furniture, Doors & Windows (one or more).",
      "Tap Save. Their card is ready for a quotation later.",
    ],
  },
  {
    id: "lead", title: "Add a new lead", blurb: "Someone called, messaged or was referred: put them in.",
    perm: "leads", to: "/leads", tour: "lead",
    steps: [
      "Open Leads.",
      "Tap “Quick add”.",
      "Type name and mobile number. The CRM warns you if the number is already a lead.",
      "Choose where they came from (source) and the division.",
      "Set a follow-up date, so the CRM reminds you.",
      "Tap Save.",
    ],
  },
  {
    id: "storewalk", title: "Quote while walking the showroom", blurb: "On the phone: add products as the customer points at them.",
    perm: "quotes", to: "/quotes/walk", tour: "storewalk",
    steps: [
      "Open Store Walk Quote.",
      "Link the customer first (search by name or phone, or add them).",
      "Search or scan each product the customer likes; tap it to add. Pick the room it is for.",
      "Something not in the list? Use “Add it by hand”.",
      "Tap “Make quotation”. Share the PDF on WhatsApp straight away.",
    ],
  },
  {
    id: "quote", title: "Make a quotation at the desk", blurb: "A full quotation with prices, discount and terms.",
    perm: "quotes", to: "/quotes", tour: "quote",
    steps: [
      "Open Deals / Quotes.",
      "Tap “New Quotation”.",
      "Pick the customer (link them: the quotation then shows on their card).",
      "Add the products: pick from stock or the catalogue, or type them in.",
      "Check the discount, GST and total at the bottom.",
      "Tap “Save Quotation”, then open the PDF to send it.",
    ],
  },
  {
    id: "followups", title: "Do today's follow-ups", blurb: "Call everyone who is due today or overdue.",
    perm: "leads", to: "/follow-ups", tour: "followups",
    steps: [
      "Open Lead Follow-ups.",
      "Start with “Overdue” (red), then “Today”.",
      "Call the customer. Then write the next action, or move the follow-up date (Tomorrow, 3 days, 1 week).",
      "When a lead is lost or won, change its stage on the lead.",
    ],
  },
  {
    id: "call", title: "Write down a phone call", blurb: "Every call in or out, so nothing is forgotten.",
    perm: "calls", to: "/calls", tour: "call",
    steps: [
      "Open Call Log.",
      "Type the phone number. If the person is in the CRM, their name comes up.",
      "Tap what happened (answered, no answer, call back…).",
      "Add a short note and tap Save.",
    ],
  },
  {
    id: "checkin", title: "Check in when you arrive", blurb: "Your attendance, from your phone at the showroom.",
    perm: "attendance", to: "/attendance", tour: "checkin",
    steps: [
      "Open Attendance.",
      "Allow location when the phone asks.",
      "When it says you are inside the showroom area, tap “Punch Check-In”.",
      "Before you leave, come back and tap “Punch Check-Out”.",
      "Need a day off? Use the Leave tab.",
    ],
  },
  {
    id: "task", title: "Plan my work", blurb: "Things to do, with a time and a reminder.",
    perm: "tasks", to: "/tasks", tour: "task",
    steps: [
      "Open Tasks.",
      "Tap “New Task”.",
      "Write what to do, pick the date, time and reminder.",
      "Tap Save. A reminder pops up on time on any screen.",
    ],
  },
  {
    id: "customer", title: "Find a customer and everything about them", blurb: "Quotes, orders, payments, visits: on one page.",
    perm: "customers", to: "/customers",
    steps: [
      "Use the search box at the top of any screen: type a name or phone number.",
      "Tap the customer. Their page shows visits, quotations, orders, payments and projects.",
      "From there you can make a quotation or add a note.",
    ],
  },
  {
    id: "catalogue", title: "Share a catalogue or price list", blurb: "Send a link on WhatsApp; it always shows the latest version.",
    perm: "quotes", to: "/catalogues",
    steps: [
      "Open Catalogues.",
      "Find the brochure or price list.",
      "Tap Share, then copy the link or send it on WhatsApp.",
    ],
  },
  {
    id: "payment", title: "Record a customer payment", blurb: "Money received against an order or invoice.",
    perm: "finance-payments", to: "/payments",
    steps: [
      "Open Payments & Tax Invoices.",
      "Tap “Record Payment”.",
      "Pick the customer and the order or invoice it is for.",
      "Enter amount, date and mode (cash, UPI, bank). Add the reference number.",
      "Tap Save. The balance due updates by itself.",
    ],
  },
  {
    id: "vendor-invoice", title: "Check a vendor's invoice against the PO", blurb: "Upload the invoice; the CRM shows what differs.",
    perm: "purchase-orders", to: "/purchase-orders",
    steps: [
      "Open Purchase Orders and tap the PO.",
      "Tap “Attach invoice” and choose the vendor's invoice (PDF, Excel or a photo).",
      "Look at the rows marked “Different”, “Not on invoice” or “Not on PO”.",
      "Tick only the changes the PO should take, then tap “Attach invoice to PO”.",
    ],
  },
];

export const SCREENS = [
  { match: "/start", title: "Start here",
    what: "The tasks you do most, each with simple steps and a “Show me” button.",
    steps: ["Tap a task to see its steps.", "“Show me” opens the right screen and points at each button.",
            "The ? Help button on every screen explains that screen."] },
  { match: "/", exact: true, title: "Dashboard",
    what: "Today at a glance: visitors, leads, quotations and what needs your attention.",
    steps: ["Tap any number to open the list behind it.", "Use the menu on the left (or the bottom bar on a phone) to go to a screen.",
            "Not sure where to start? Open “Start here” from the menu."] },
  { match: "/visitors", title: "Visitors", task: "walkin",
    tips: ["Type the phone number first: it finds a customer who came before, so you don't make a second card.",
           "Write who referred them (the architect). This is how architects' referrals are counted.",
           "Log every walk-in, even if they only look around."] },
  { match: "/leads", title: "Leads", task: "lead",
    tips: ["Always set a follow-up date. Leads without one are listed as “No follow-up date” in Follow-ups.",
           "Use the column filters to find your own leads quickly.", "Open a lead to change its stage, add a note or make a quotation."] },
  { match: "/follow-ups", title: "Lead Follow-ups", task: "followups",
    tips: ["Red (Overdue) first, then Today.", "After every call, write the next action or move the date: an empty next action shows up here again."] },
  { match: "/quotes/walk", title: "Store Walk Quote", task: "storewalk",
    tips: ["Link the customer before you start adding products.", "“In our store” is stock on the floor; “Vendor catalogues” are items made to order.",
           "An amber label like “1 in stock · 1 to order” means there isn't enough in stock: tell the customer the rest is made to order."] },
  { match: "/quotes/ws", title: "Quotation", task: "quote",
    tips: ["Check the total and discount at the bottom before sending.", "Open the PDF to see exactly what the customer will get."] },
  { match: "/quotes/followups", title: "Quote Follow-ups",
    what: "Quotations sent and not yet answered.",
    steps: ["Call the customer about each quotation.", "Mark it won or lost, or set the next follow-up date."] },
  { match: "/quotes", title: "Deals / Quotes", task: "quote",
    tips: ["A quotation linked to the customer shows on their page.", "Once the customer says yes, convert the quotation into an order.",
           "Each stock item shows what is in stock. Amber “… to order” means not enough: tell the customer the rest is made to order."] },
  { match: "/calls", title: "Call Log", task: "call",
    tips: ["Write down calls you missed too (“No answer”): the CRM can remind you to call back."] },
  { match: "/attendance", title: "Attendance", task: "checkin",
    tips: ["Checked in at the wrong place? Ask your manager to correct it.", "The calendar shows your month: P present, L late, HD half day, A absent."] },
  { match: "/tasks", title: "Tasks", task: "task",
    tips: ["Give the task a time to get a reminder pop-up."] },
  { match: "/daily-planner", title: "Daily Planner",
    what: "Your whole day hour by hour: your tasks and lead follow-ups, meetings, quotations to follow up, goods to receive and deliveries to make.",
    steps: ["Use the arrows to move between days.", "Quick add puts a task on the day you are looking at.",
      "Tap an hour (the + sign) to add a task at that time; it reminds you 15 minutes before.",
      "Tap the circle to tick a task done; tap the task to move it to another time or day."],
    tips: ["“Still open from earlier days” lists what you didn't finish: tap “Plan for today”.",
      "Goods in / Delivery cards open the Goods Receipts or Delivery Challans screen for that job."] },
  { match: "/goods-receipts", title: "Goods Receipts",
    what: "What the store is waiting for. Every issued purchase order is expected here and goes step by step: Expected, Arrived, Checked, Received.",
    steps: ["When the goods come, tap “Goods arrived”.", "Count them and look for damage, then tap “Checked” (write down anything wrong).",
      "Tap “Receive goods” to put them into stock."],
    tips: ["Each step puts a task on the store person's day, and ticks it off when the next step is done.",
      "“Change” gives the job to someone else or moves the expected date; their task moves too."] },
  { match: "/stock-counts", title: "Stock Counts",
    what: "Count what is on the shelves and compare it with the CRM. The stock is corrected only after someone else approves the count.",
    steps: ["Tap “New count” and pick a floor or a category (or leave both empty for all stock).",
      "Walk the floor and type how many you find of each item. Each number is saved when you leave the box.",
      "Found something that isn't on the list? Add it with “Add an item found”.",
      "When you're done, tap “Send for approval”. The approver checks the differences and approves or rejects."],
    tips: ["The CRM remembers its own number at the moment you count, so sales made later are never undone.",
      "Approving posts only the differences, as Adjustments on the Stock Ledger.",
      "The person who counted can't approve their own count."] },
  { match: "/work-schedule", title: "Work Scheduler",
    what: "Everyone's day hour by hour: who is doing what, and when. Admins and team managers see their people; everyone else sees their own day.",
    steps: ["Use the arrows or the date to pick a day. “Week” shows Monday to Sunday for everyone.",
      "Drag a task to another person or hour (or, in Week, another day) to move it.",
      "Tap an empty hour (the + sign) to give someone a task at that time.", "Tap a task to change it or tick it done."],
    tips: ["“Still open from earlier days” lists unfinished work: drag it onto today or tap “Plan for today”.",
      "A task given a time pops up for the person 15 minutes before.", "On a phone, “By person” shows each person's day as a list.",
      "“Goods in & out” shows deliveries coming from vendors and going to customers, with who is handling them."] },
  { match: "/customers", title: "Customers", task: "customer",
    tips: ["Search by phone number: it's the surest way to find the right person."] },
  { match: "/catalogues", title: "Catalogues", task: "catalogue" },
  { match: "/virtual-catalogue", title: "Virtual Catalogue",
    what: "Products our vendors make to order, with MADIO prices.",
    steps: ["Search or filter to find a product.", "Tap it to see pictures, sizes and price.", "Add it to a lead's shortlist or straight to a quotation."] },
  { match: "/payments", title: "Payments & Tax Invoices", task: "payment" },
  { match: "/purchase-orders", title: "Purchase Orders", task: "vendor-invoice",
    tips: ["“Read vendor document” turns a vendor's quotation or proforma into a new PO.",
           "“Attach invoice” checks the vendor's invoice against a PO you already have."] },
  { match: "/sales", title: "Sales Register",
    what: "Orders the customers confirmed.",
    steps: ["Open an order to see its items, payments and delivery.", "Use Delivery Challans when goods go out."] },
];

export const TOURS = {
  walkin: [
    { to: "/visitors", target: ADD, text: "When a customer walks in, tap “Log Visitor”.", waitClick: true },
    { target: '[data-testid="vf-phone"]', text: "Type their mobile number first. If they visited before, the CRM finds them." },
    { target: '[data-testid="visitor-save"]', text: "Fill in name, area and what they need, then tap Save." },
  ],
  lead: [
    { to: "/leads", target: '[data-testid="leads-quick-add"]', text: "Tap “Quick add” to put in a new lead.", waitClick: true },
    { target: '[data-testid="form-save"]', text: "Type name and phone, choose the source, set a follow-up date, then tap Save." },
  ],
  storewalk: [
    { to: "/quotes/walk", target: '[data-testid="walk-customer"]', text: "First link the customer: search by name or phone, or add them." },
    { target: '[data-testid="walk-search"]', text: "Search for each product the customer likes and tap it to add it." },
    { target: '[data-testid="walk-manual"]', text: "Not in the list? Add it by hand here." },
    { target: '[data-testid="walk-basket-bar"]', text: "Everything chosen is here. Tap “Make quotation” to finish and share the PDF." },
  ],
  quote: [
    { to: "/quotes", target: ADD, text: "Tap “New Quotation”.", waitClick: true },
    { target: '[data-testid="quote-form-gallery"]', text: "Add products from the picture gallery, or type them in the lines below." },
    { target: '[data-testid="quote-form-subtotal"]', text: "Check the total here." },
    { target: '[data-testid="quote-save"]', text: "Tap “Save Quotation”. Then open its PDF to send it." },
  ],
  followups: [
    { to: "/follow-ups", target: '[data-testid="bucket-overdue"]', text: "Start with Overdue: these customers were due a call already." },
    { target: '[data-testid="bucket-today"]', text: "Then everyone due today." },
    { target: '[data-testid="followups-page"]', text: "After each call, write the next action or move the date with the buttons on the lead." },
  ],
  call: [
    { to: "/calls", target: '[data-testid="call-phone"]', text: "Type the phone number. A customer already in the CRM is found." },
    { target: '[data-testid="call-outcomes"]', text: "Tap what happened on the call." },
    { target: '[data-testid="call-save"]', text: "Add a short note and tap Save." },
  ],
  checkin: [
    { to: "/attendance", target: '[data-testid="geofence-verdict"]', text: "This shows whether you are inside the showroom area. Allow location if the phone asks." },
    { target: '[data-testid="att-checkin"]', text: "Tap “Punch Check-In” when you arrive. Come back to check out before you leave." },
  ],
  task: [
    { to: "/tasks", target: ADD, text: "Tap “New Task”.", waitClick: true },
    { target: '[data-testid="task-title"]', text: "Write what needs doing." },
    { target: '[data-testid="task-save"]', text: "Pick the date, time and reminder, then tap Save." },
  ],
};

export const taskById = (id) => TASKS.find((t) => t.id === id);

/** The Help panel's content for an address: the screen's own guide (its task's steps when it names one), or a short
 * general one. */
export function screenGuide(pathname) {
  const path = pathname || "/";
  const s = SCREENS.find((g) => (g.exact ? path === g.match : path === g.match || path.startsWith(`${g.match}/`)
    || (g.match !== "/" && path.startsWith(g.match))));
  if (!s) return null;
  const task = s.task ? taskById(s.task) : null;
  return {
    title: s.title,
    what: s.what || task?.blurb || "",
    steps: s.steps || task?.steps || [],
    tips: s.tips || [],
    tour: s.tour || task?.tour || null,
    taskTitle: task?.title || "",
  };
}
